import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { trainingApi, reportApi } from '../../api'
import type { ApiError } from '../../api/client'
import type { TrainingProjection } from '../../types/api'
import { useTrainingSetData } from '../../hooks/useTrainingSetData'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../contexts/AuthContext'
import { formatLongDate } from '../../utils/dateTranslations'
import { TIME_FORMAT_OPTIONS } from '../../constants'
import {
  getNextTraining,
  canCompleteTrainingSet,
  groupTrainingsByWeek,
  getWeekDateRange,
  formatDateRange
} from '../../utils/trainingUtils'
import { TrainingSetActions } from './TrainingSetActions'
import { CompleteSetModal } from './CompleteSetModal'
import { captureEvent } from '../../posthog'
import { PauseSetModal } from './PauseSetModal'
import { StatusBadge, Loading, EmptyState, Avatar, Card } from '../ui'
import { TrainingProjectionRow } from './TrainingProjectionRow'
import { PulsingExpandButton } from '../ui/PulsingExpandButton'
import { TRAINING_SET_STATUS } from '../../constants'
import { useTour } from '../../onboarding/TourProvider'
import {
  TRAINING_SET_DETAIL_COACH_TOUR_ID,
  TRAINING_SET_DETAIL_TRAINEE_TOUR_ID,
} from '../../onboarding/tours/trainingSetDetailTour'

export default function TrainingSetDetail() {
  const navigate = useNavigate()
  const { traineeId, setId } = useParams<{ traineeId: string; setId: string }>()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useAuth()

  const [deleting, setDeleting] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [showCompleteModal, setShowCompleteModal] = useState(false)
  const [autoProlongation, setAutoProlongation] = useState(false)
  const [sendingReport, setSendingReport] = useState(false)
  const [showPastTrainings, setShowPastTrainings] = useState(false)
  const [pausing, setPausing] = useState(false)
  const [resuming, setResuming] = useState(false)
  const [cancelingPause, setCancelingPause] = useState(false)
  const [showPauseModal, setShowPauseModal] = useState(false)
  const [pauseStartDate, setPauseStartDate] = useState('')
  const [pauseEndDate, setPauseEndDate] = useState('')

  const { trainings, loading, trainingSet, relatedUser, stats, refetch } = useTrainingSetData(setId)
  const { startTour, activeTourId } = useTour()
  const isCurrentUserTrainee = !!(user?.id && trainingSet?.traineeId && user.id === trainingSet.traineeId)
  const viewOnly = isCurrentUserTrainee
  const startedTourRef = useRef<string | null>(null)

  useEffect(() => {
    if (loading || !user || !trainingSet || trainings.length === 0) return
    const tourId = isCurrentUserTrainee
      ? TRAINING_SET_DETAIL_TRAINEE_TOUR_ID
      : TRAINING_SET_DETAIL_COACH_TOUR_ID
    if (activeTourId === tourId) startedTourRef.current = tourId
    if (startedTourRef.current === tourId) return
    if (startTour(tourId)) startedTourRef.current = tourId
  }, [loading, user, trainingSet, trainings.length, isCurrentUserTrainee, startTour, activeTourId])
  const [updatingUnpaid, setUpdatingUnpaid] = useState(false)

  const handleDeleteSet = async () => {
    if (!traineeId || !setId) return
    if (!confirm(t.trainingSetDetail.confirmations.delete)) return
    setDeleting(true)
    try {
      await trainingApi.deleteTrainingSet(parseInt(traineeId), parseInt(setId))
      captureEvent('training_set_deleted', { trainee_id: Number(traineeId), set_id: Number(setId) })
      alert(t.trainingSetDetail.messages.deleteSuccess)
      navigate(`/trainee/${traineeId}`)
    } catch (err) {
      console.error('Error deleting training set:', err)
      const apiError = err as ApiError
      alert(apiError?.message || t.trainingSetDetail.errors.deleteFailed)
    } finally {
      setDeleting(false)
    }
  }

  const handleCompleteSet = async () => {
    if (!traineeId || !setId) return
    setCompleting(true)
    try {
      await trainingApi.completeTrainingSet(parseInt(traineeId), parseInt(setId), autoProlongation)
      captureEvent('training_set_completed', {
        trainee_id: Number(traineeId),
        set_id: Number(setId),
        auto_prolongation: autoProlongation,
      })
      alert(autoProlongation ? t.trainingSetDetail.messages.completeWithProlongation : t.trainingSetDetail.messages.completeSuccess)
      navigate(`/trainee/${traineeId}`)
    } catch (err) {
      console.error('Error completing training set:', err)
      const apiError = err as ApiError
      alert(apiError?.message || t.trainingSetDetail.errors.completeFailed)
    } finally {
      setCompleting(false)
      setShowCompleteModal(false)
      setAutoProlongation(false)
    }
  }

  const handleGetReport = async () => {
    if (!setId) return
    setSendingReport(true)
    try {
      await reportApi.sendTrainingSetReport(parseInt(setId))
      alert(t.trainingSetDetail.messages.reportSent)
    } catch (err) {
      console.error('Error sending report:', err)
      const apiError = err as ApiError
      alert(apiError?.message || t.trainingSetDetail.errors.reportFailed)
    } finally {
      setSendingReport(false)
    }
  }

  const handlePauseSet = async () => {
    if (!setId) return
    setPausing(true)
    try {
      await trainingApi.pauseTrainingSet(parseInt(setId), pauseStartDate, pauseEndDate)
      alert(t.trainingSetDetail.messages.pauseSuccess)
      setShowPauseModal(false)
      setPauseStartDate('')
      setPauseEndDate('')
      window.location.reload()
    } catch (err) {
      console.error('Error pausing training set:', err)
      const apiError = err as ApiError
      alert(apiError?.message || t.trainingSetDetail.errors.pauseFailed)
    } finally {
      setPausing(false)
    }
  }

  const handleResumeSet = async () => {
    if (!setId) return
    if (!confirm(t.trainingSetDetail.confirmations.resume)) return
    setResuming(true)
    try {
      await trainingApi.resumeTrainingSet(parseInt(setId))
      alert(t.trainingSetDetail.messages.resumeSuccess)
      window.location.reload()
    } catch (err) {
      console.error('Error resuming training set:', err)
      const apiError = err as ApiError
      alert(apiError?.message || t.trainingSetDetail.errors.resumeFailed)
    } finally {
      setResuming(false)
    }
  }

  const handleCancelPause = async () => {
    if (!setId) return
    if (!confirm(t.trainingSetDetail.confirmations.cancelPause)) return
    setCancelingPause(true)
    try {
      await trainingApi.cancelPause(parseInt(setId))
      alert(t.trainingSetDetail.messages.cancelPauseSuccess)
      window.location.reload()
    } catch (err) {
      console.error('Error canceling pause:', err)
      const apiError = err as ApiError
      alert(apiError?.message || t.trainingSetDetail.errors.cancelPauseFailed)
    } finally {
      setCancelingPause(false)
    }
  }

  const handleToggleUnpaid = async () => {
    if (!setId || !trainingSet) return
    const nextUnpaid = trainingSet.unpaid !== true
    setUpdatingUnpaid(true)
    try {
      await trainingApi.updateTrainingSetUnpaid(parseInt(setId), nextUnpaid)
      alert(nextUnpaid
        ? t.trainingSetDetail.messages.markUnpaidSuccess
        : t.trainingSetDetail.messages.clearUnpaidSuccess)
      await refetch()
    } catch (err) {
      console.error('Error updating unpaid flag:', err)
      const apiError = err as ApiError
      alert(apiError?.message || t.trainingSetDetail.errors.unpaidFailed)
    } finally {
      setUpdatingUnpaid(false)
    }
  }

  const isPaused = !!(trainingSet?.pauseStartDate && trainingSet?.pauseEndDate)

  const nextTraining = getNextTraining(trainings)
  const canComplete = canCompleteTrainingSet(trainings, trainingSet?.status || null)

  const groupedAllByWeek = groupTrainingsByWeek(trainings)

  const now = new Date()
  const pastTrainings = trainings.filter(t => new Date(t.end) < now)

  const groupedPastByWeek: Record<number, TrainingProjection[]> = {}
  const groupedFutureByWeek: Record<number, TrainingProjection[]> = {}

  Object.entries(groupedAllByWeek).forEach(([weekNum, weekTrainings]) => {
    const weekNumber = parseInt(weekNum)
    const pastInWeek = weekTrainings.filter(t => new Date(t.end) < now)
    const futureInWeek = weekTrainings.filter(t => new Date(t.end) >= now)
    if (pastInWeek.length > 0) groupedPastByWeek[weekNumber] = pastInWeek
    if (futureInWeek.length > 0) groupedFutureByWeek[weekNumber] = futureInWeek
  })

  if (loading) {
    return <Loading message={t.trainingSetDetail.loading} fullScreen />
  }

  if (trainings.length === 0) {
    return (
      <div className="p-5">
        <header className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: theme.text.primary }}>
            {t.trainingSetDetail.title}
          </h1>
        </header>
        <EmptyState message={t.trainingSetDetail.emptyState.title} />
      </div>
    )
  }

  const sectionLabel = {
    fontSize: '11px' as const,
    fontWeight: 700 as const,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: theme.text.muted,
    opacity: 0.7,
  }

  const isCompleted = trainingSet?.status === TRAINING_SET_STATUS.COMPLETED

  function renderWeekGroup(weekNum: string, weekTrainings: TrainingProjection[]) {
    const weekNumber = parseInt(weekNum)
    const weekRange = getWeekDateRange(weekNumber, trainings[0])
    return (
      <div key={weekNum}>
        <div className="flex items-baseline gap-2 mb-2">
          <p style={sectionLabel}>{t.trainingSetDetail.schedule.week} {weekNum}</p>
          <p className="text-[10px]" style={{ color: theme.text.muted, opacity: 0.6 }}>
            {formatDateRange(weekRange.start, weekRange.end, t)}
          </p>
        </div>
        <Card className="p-0 overflow-hidden">
          {weekTrainings.map((training, i) => {
            const trainingIndex = trainings.findIndex(tr => tr.id === training.id)
            const displayNumber = trainingIndex >= 0 ? trainingIndex + 1 : i + 1
            const isClickable = !viewOnly && !isCompleted && !!training.id
            return (
              <TrainingProjectionRow
                key={training.id || i}
                training={training}
                displayNumber={displayNumber}
                showBorderBottom={i < weekTrainings.length - 1}
                isNext={nextTraining?.id === training.id}
                onClick={isClickable
                  ? () => navigate(`/trainee/${traineeId}/set/${setId}/training/${training.id}`)
                  : undefined}
              />
            )
          })}
        </Card>
      </div>
    )
  }

  return (
    <div className="p-5 pb-24">
      <header className="mb-6" data-tour="tour-set-overview">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <h1 className="text-2xl font-bold" style={{ color: theme.text.primary }}>
            {t.trainingSetDetail.title}
          </h1>
          {trainingSet?.unpaid === true && (
            <StatusBadge
              status={t.allTrainingSets.unpaid}
              variant="warning"
              size="sm"
            />
          )}
        </div>
        {stats && (
          <p className="text-sm" style={{ color: theme.text.muted }}>
            {t.trainingSetDetail.subtitle.replace('{{count}}', String(stats.plannedCount))}
          </p>
        )}
      </header>

      {/* Related user */}
      {relatedUser && (
        <section className="mb-5">
          <button
            type="button"
            onClick={() => navigate(isCurrentUserTrainee ? `/coach/${relatedUser.id}` : `/trainee/${relatedUser.id}`)}
            className="w-full rounded-xl p-4 flex items-center gap-3 text-left transition-opacity hover:opacity-90 active:opacity-70"
            style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
          >
            <Avatar
              photoUrl={relatedUser.photoUrl}
              name={[relatedUser.firstName, relatedUser.lastName].filter(Boolean).join(' ') || relatedUser.username || ''}
              size="md"
              fallbackBg={theme.accent.light}
              className="flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-xs mb-0.5" style={{ color: theme.text.muted }}>
                {isCurrentUserTrainee ? t.trainingSetDetail.relatedUser.coachLabel : t.trainingSetDetail.relatedUser.traineeLabel}
              </p>
              <p className="text-base font-semibold truncate" style={{ color: theme.text.primary }}>
                {[relatedUser.firstName, relatedUser.lastName].filter(Boolean).join(' ') || relatedUser.username || `ID ${relatedUser.id}`}
              </p>
              {relatedUser.username && (
                <p className="text-xs truncate" style={{ color: theme.text.muted }}>@{relatedUser.username}</p>
              )}
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0" style={{ color: theme.text.muted }}>
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </section>
      )}

      {/* Pause indicator */}
      {isPaused && trainingSet.pauseStartDate && trainingSet.pauseEndDate && (
        <section
          className="mb-5 p-4 rounded-xl flex items-start gap-3"
          style={{ backgroundColor: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.28)' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5" style={{ color: 'rgba(249,115,22,1)' }}>
            <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
          </svg>
          <div>
            <p className="text-sm font-semibold mb-0.5" style={{ color: 'rgba(249,115,22,1)' }}>
              {t.trainingSetDetail.pause.title}
            </p>
            <p className="text-xs" style={{ color: 'rgba(249,115,22,0.85)' }}>
              {t.trainingSetDetail.pause.period}: {formatLongDate(new Date(trainingSet.pauseStartDate), t)} – {formatLongDate(new Date(trainingSet.pauseEndDate), t)}
            </p>
          </div>
        </section>
      )}

      {/* Progress */}
      {stats && (
        <section
          data-tour="tour-set-progress"
          className="mb-5 p-4 rounded-xl"
          style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
        >
          <p className="text-xs font-semibold mb-3" style={{ color: theme.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {t.trainingSetDetail.progress.title}
          </p>

          {(() => {
            const totalCounted = stats.completedCount + stats.totalFiredCount + stats.futureCount
            const completedPct = totalCounted > 0 ? (stats.completedCount / totalCounted) * 100 : 0
            const firedPct = totalCounted > 0 ? (stats.totalFiredCount / totalCounted) * 100 : 0
            const futurePct = totalCounted > 0 ? (stats.futureCount / totalCounted) * 100 : 0
            const remainingStart = completedPct + firedPct
            const blueDone = theme.buttonColor || '#0088cc'
            const blueFired = 'rgba(0,136,204,0.55)'
            const blueFuture = 'rgba(0,136,204,0.22)'

            return (
              <>
                <div className="h-4 rounded-full overflow-visible relative mb-3" style={{ backgroundColor: 'rgba(128,128,128,0.2)' }}>
                  <div className="h-full flex rounded-full overflow-hidden">
                    {completedPct > 0 && (
                      <div
                        className="h-full flex items-center justify-center text-xs font-semibold text-white transition-all duration-500"
                        style={{ width: `${completedPct}%`, backgroundColor: blueDone, minWidth: '2%' }}
                      >
                        {completedPct > 10 && stats.completedCount}
                      </div>
                    )}
                    {firedPct > 0 && (
                      <div
                        className="h-full flex items-center justify-center text-xs font-semibold text-white transition-all duration-500"
                        style={{ width: `${firedPct}%`, backgroundColor: blueFired, minWidth: '2%' }}
                      >
                        {firedPct > 10 && stats.totalFiredCount}
                      </div>
                    )}
                    {futurePct > 0 && (
                      <div
                        className="h-full flex items-center justify-center text-xs font-semibold transition-all duration-500"
                        style={{ width: `${futurePct}%`, backgroundColor: blueFuture, color: theme.accent.text, minWidth: '2%' }}
                      >
                        {futurePct > 10 && stats.futureCount}
                      </div>
                    )}
                  </div>
                  {futurePct > 0 && (completedPct > 0 || firedPct > 0) && (
                    <div
                      className="absolute flex items-center justify-center"
                      style={{ left: `${remainingStart}%`, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}
                    >
                      <div
                        className="absolute"
                        style={{ width: '2px', height: '20px', backgroundColor: blueDone, top: '-10px', boxShadow: `0 0 4px ${blueDone}` }}
                      />
                      <div
                        className="absolute flex items-center justify-center"
                        style={{ top: '-20px', backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '50%', width: '24px', height: '24px', boxShadow: '0 2px 6px rgba(0,0,0,0.25)', border: `2px solid ${blueDone}` }}
                      >
                        <span style={{ fontSize: '14px', lineHeight: '1' }}>⚡</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1.5 text-xs">
                  <p style={{ color: theme.text.muted }}>{t.trainingSetDetail.progress.planned}: <span className="font-semibold" style={{ color: theme.text.primary }}>{stats.plannedCount}</span></p>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded shrink-0" style={{ backgroundColor: blueDone }} />
                    <p style={{ color: theme.text.muted }}>{t.trainingSetDetail.progress.completed}: <span className="font-semibold" style={{ color: theme.text.primary }}>{stats.completedCount}</span></p>
                  </div>
                  {stats.totalFiredCount > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded shrink-0" style={{ backgroundColor: blueFired }} />
                      <p style={{ color: theme.text.muted }}>{t.trainingSetDetail.progress.fired}: <span className="font-semibold" style={{ color: theme.text.primary }}>{stats.totalFiredCount}</span></p>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded shrink-0" style={{ backgroundColor: blueFuture }} />
                    <p style={{ color: theme.text.muted }}>{t.trainingSetDetail.progress.remaining}: <span className="font-semibold" style={{ color: theme.text.primary }}>{stats.futureCount}</span></p>
                  </div>
                  {((stats.skippedCount ?? 0) + (stats.cancelledCount ?? 0)) > 0 && (
                    <div
                      className="flex items-center gap-1.5 pt-2 mt-1"
                      style={{ borderTop: `1px solid ${theme.cardBorder}` }}
                    >
                      <div className="w-2.5 h-2.5 rounded shrink-0" style={{ backgroundColor: 'rgba(128,128,128,0.4)' }} />
                      <p style={{ color: theme.text.muted }}>
                        {t.trainingSetDetail.progress.skippedCancelled}: {(stats.skippedCount ?? 0) + (stats.cancelledCount ?? 0)}
                        <span className="opacity-60 ml-1">({t.trainingSetDetail.progress.notCounted})</span>
                      </p>
                    </div>
                  )}
                </div>
              </>
            )
          })()}
        </section>
      )}

      {/* Next training */}
      {nextTraining && (
        <section
          className="mb-5 rounded-xl"
          style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
        >
          <div className="flex items-center justify-between px-4 py-3.5">
            <div className="flex items-center gap-3">
              <span
                className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${theme.buttonColor}20`, color: theme.accent.text }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </span>
              <div>
                <p className="text-xs font-semibold mb-0.5" style={{ color: theme.text.muted }}>
                  {t.trainingSetStats.nextTraining}
                </p>
                <p className="text-sm font-semibold" style={{ color: theme.text.primary }}>
                  {formatLongDate(new Date(nextTraining.start), t)}
                </p>
              </div>
            </div>
            <p className="text-sm font-semibold tabular-nums" style={{ color: theme.text.primary }}>
              {new Date(nextTraining.start).toLocaleTimeString('uk-UA', TIME_FORMAT_OPTIONS.short)}
              {' – '}
              {new Date(nextTraining.end).toLocaleTimeString('uk-UA', TIME_FORMAT_OPTIONS.short)}
            </p>
          </div>
        </section>
      )}

      {/* Actions */}
      {!viewOnly && (
        <TrainingSetActions
          canComplete={canComplete}
          isCompleting={completing}
          isDeleting={deleting}
          isPaused={!!isPaused}
          isPausing={pausing}
          isResuming={resuming}
          isCancelingPause={cancelingPause}
          isSendingReport={sendingReport}
          isUnpaid={trainingSet?.unpaid === true}
          isUpdatingUnpaid={updatingUnpaid}
          onComplete={() => setShowCompleteModal(true)}
          onDelete={handleDeleteSet}
          onPause={() => setShowPauseModal(true)}
          onResume={handleResumeSet}
          onCancelPause={handleCancelPause}
          onGetReport={handleGetReport}
          onToggleUnpaid={handleToggleUnpaid}
        />
      )}

      {/* Schedule */}
      <div className="flex flex-col gap-5">
        {Object.keys(groupedPastByWeek).length > 0 && (
          <div className="flex flex-col gap-3">
            <PulsingExpandButton
              label={t.trainingSetDetail.schedule.pastTrainings}
              count={pastTrainings.length}
              countLabel={t.trainingSetDetail.schedule.training}
              countLabelPlural={t.trainingSetDetail.schedule.trainings}
              isExpanded={showPastTrainings}
              onToggle={() => setShowPastTrainings(!showPastTrainings)}
            />
            {showPastTrainings && (
              <div className="flex flex-col gap-5">
                {Object.entries(groupedPastByWeek).map(([weekNum, weekTrainings]) =>
                  renderWeekGroup(weekNum, weekTrainings)
                )}
              </div>
            )}
          </div>
        )}

        {Object.entries(groupedFutureByWeek).map(([weekNum, weekTrainings]) =>
          renderWeekGroup(weekNum, weekTrainings)
        )}
      </div>

      <CompleteSetModal
        isOpen={showCompleteModal}
        isCompleting={completing}
        autoProlongation={autoProlongation}
        onAutoProlongationChange={setAutoProlongation}
        onConfirm={handleCompleteSet}
        onCancel={() => {
          setShowCompleteModal(false)
          setAutoProlongation(false)
        }}
      />

      <PauseSetModal
        isOpen={showPauseModal}
        isPausing={pausing}
        pauseStartDate={pauseStartDate}
        pauseEndDate={pauseEndDate}
        onPauseStartDateChange={setPauseStartDate}
        onPauseEndDateChange={setPauseEndDate}
        onConfirm={handlePauseSet}
        onCancel={() => {
          setShowPauseModal(false)
          setPauseStartDate('')
          setPauseEndDate('')
        }}
      />
    </div>
  )
}
